<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2
        id="targeted-prenatal-test-title"
        style="display: flex; gap: 9px; flex-wrap: wrap; font-size: 25px;"
      >
        <span>Prenatal targeted whole genome sequencing (WGS)</span>
        <span>(familial variant testing)</span>
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="targeted-prenatal-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Funding source*</span>
              <v-select
                item-disabled=""
                v-model="billTo"
                :items="billingOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.billTo'"
                outlined
                :rules="ruleNotNull"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family type*</span>
              <v-select
                v-model="familyType"
                :items="familyTypes"
                :id="testId + '.familyType'"
                no-data-text="Select a turn around time first"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col class="custom-col">
              <span class="text-title"
                >Family member previously tested at VCGS*</span
              >
              <v-select
                v-model="familyPrevTested"
                :items="yesNoOptions"
                item-text="key"
                :rules="ruleValidBoolean"
                item-value="value"
                :id="testId + '.familyPrevTested'"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="familyPrevTested === true">
            <v-col class="custom-col">
              <span class="text-title">VCGS sample ID</span>
              <v-text-field
                v-model="posCtrlSampleId"
                :id="testId + '.posCtrlSampleId'"
                outlined
                hide-details="auto"
              ></v-text-field>
              <span class="text-hint"
                >e.g. 20C100123. This is located in the top right section of our
                reports.</span
              >
            </v-col>
          </v-row>
          <v-row v-if="familyPrevTested === true">
            <v-col class="custom-col">
              <span class="text-title">Family member name</span>
              <v-text-field
                v-model="posCtrlName"
                :id="testId + '.posCtrlName'"
                outlined
                hide-details="auto"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="familyPrevTested === true">
            <v-col class="custom-col">
              <span class="text-title">Family member date of birth</span>
              <v-menu
                v-model="familyMemberDOBMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="familyMemberDOBDisplay"
                    :id="testId + '.posCtrlBirthDate'"
                    v-on="on"
                    :rules="familyMemberDOBDisplay != '' ? ruleDate : []"
                    outlined
                    hide-details="auto"
                  ></v-text-field>
                </template>
                <v-date-picker
                  locale="en-au"
                  :min="minDate"
                  :max="maxDate"
                  v-model="familyDOBVal"
                  @input="familyMemberDOBMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <UploadComponent
            v-if="familyPrevTested !== true"
            :testId="testId"
            :testName="testName"
            :updateOnDestroy="true"
            @updateUploadObj="updateUploadObj"
            uploadType="previousReport"
          />

          <v-row>
            <v-col class="custom-col">
              <span class="text-title"
                >Relationship of this fetus to the positive control*</span
              >
              <v-text-field
                v-model="rhipToPosCtrl"
                :id="testId + '.rhipToPosCtrl'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-text-field>
              <span class="text-hint">e.g. sibling, child, aunt, etc.</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Number of variants to be tested*</span>
              <v-select
                v-model="numVariants"
                :items="numVariantsRange"
                :id="testId + '.numVariants'"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title"
                >Clinical notes, variant details, and expected amnio/CVS
                procedure date (if relevant)</span
              >
              <v-textarea
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, increased chance on carrier screening,
                ultrasound findings, biochemistry etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                recurrent miscarriage, etc.</span
              >
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                style="grid-area: person1"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="prenatal_targeted_genome_parents"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                style="grid-area: person2"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="prenatal_targeted_genome_parents"
              />
            </v-col>
          </v-row>
        </v-container>

        <PrenatalConsentContainer
          ref="PrenatalConsentContainer"
          testName="Prenatal targeted WGS (familial variant testing)"
          :is-trio="familyType === trioFlag"
          :force-form-dirty="forceFormDirty"
        ></PrenatalConsentContainer>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="targeted_prenatal_continue_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../../FamilyMember";
import FormRulesMixin from "../../../mixins/FormRulesMixin";
import { EventBus } from "@/event-bus";
import {
  useCompletedDetails,
  useSectionStore,
} from "../../../store/SectionStore";
import PrenatalConsentContainer from "./PrenatalConsentContainer";
import TestPriceContainer from "../../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../../mixins/RetrieveJSONMixin.vue";
import UploadComponent from "../../upload/UploadComponent.vue";
import UploadMixin from "../../../mixins/UploadMixin.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyDOBVal: function() {
      this.familyMemberDOBDisplay = this.familyDOBVal
        ? this.formatDate(this.familyDOBVal)
        : "";
    },
    familyType: function(newVal) {
      // When it is trio test, update selectedTrioTests in App component
      if (newVal === "Trio") {
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.targetedPrenatalTestPricing = await this.execRetrieveJson(
      "json/pricing/prenatal_targeted_wgs_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");
    const element = document.getElementById("targeted-prenatal-test-title");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },

  mixins: [FormRulesMixin, RetrieveJSONMixin, UploadMixin],

  name: "",

  components: {
    FamilyMember,
    PrenatalConsentContainer,
    TestPriceContainer,
    UploadComponent,
  },

  data: () => ({
    valid: false,
    posCtrlSampleId: "",
    urgentReason: "",
    targetedPrenatalTestPricing: null,
    familyHx: "",
    clinicalNotes: "",
    familyPrevTested: null,
    medicare: false,
    familyType: "",
    familyTypes: ["Singleton", "Trio"],
    trioFlag: "Trio",
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    billingOptions: [
      { key: "Patient / Family", value: "PATIENT" },
      {
        key: "Clinical Service / Institution / Hospital",
        value: "PRACTITIONER",
      },
    ],
    billTo: "",
    posCtrlName: "",
    familyMemberDOBMenu: false,
    minDate: "1801-01-01",
    maxDate: new Date().toJSON().slice(0, 10),
    familyDOBVal: null,
    familyMemberDOBDisplay: "",
    rhipToPosCtrl: "",
    numVariantsRange: [1, 2, 3, 4],
    numVariants: "",
  }),

  computed: {
    isFamSingleton() {
      return this.familyType === "Singleton";
    },
    getPriceDetails() {
      if (!this.targetedPrenatalTestPricing) {
        EventBus.$emit("trioTargetedPrenatalTestAlert", false);
        return null;
      }

      if (!this.isFamSingleton) {
        // Trio family (prev tested or not), show reanalysis alert

        EventBus.$emit("trioTargetedPrenatalTestAlert", true);
        return 0.0;
      } else if (this.familyPrevTested && this.isFamSingleton) {
        // Singleton family and is prev tested

        EventBus.$emit("trioTargetedPrenatalTestAlert", false);
        return this.targetedPrenatalTestPricing["internalVariant"]["Singleton"][
          "price"
        ];
      } else if (!this.familyPrevTested && this.isFamSingleton) {
        // Singleton family and is not prev tested

        EventBus.$emit("trioTargetedPrenatalTestAlert", false);
        const oneVariantPricing = this.targetedPrenatalTestPricing[
          "externalVariant"
        ]["Singleton"]["price"];

        // One additional variant increase 300 dollars
        return oneVariantPricing + 300 * parseFloat(this.numVariants);
      } else {
        EventBus.$emit("trioTargetedPrenatalTestAlert", false);
        return 0.0;
      }
    },
    price() {
      return this.getPriceDetails;
    },
    testCode() {
      if (!this.isFamSingleton) {
        return "prenatal_targeted_genome_trio";
      } else {
        return "prenatal_targeted_genome_singleton";
      }
    },
  },

  methods: {
    isFormValid: function() {
      let isValid;
      if (typeof this.$refs.PrenatalConsentContainer !== "undefined") {
        isValid = this.$refs.PrenatalConsentContainer.isFormValid();
      } else isValid = false;
      if (this.familyType === this.trioFlag) {
        return (
          isValid &&
          this.$refs.familyMember1.isFormValid() &&
          this.$refs.familyMember2.isFormValid()
        );
      } else return isValid;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    formatDate: function(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    reverseFormatDate(date) {
      if (!date) return null;
      const [day, month, year] = date.split("/");

      return `${year}-${month}-${day}`;
    },
    transferData: function() {
      let familyDetails = [];
      if (this.familyType === "Trio") {
        for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
          if (
            familyComponent &&
            familyKey.substring(0, 12) === "familyMember"
          ) {
            familyDetails.push(familyComponent.transferData());
          }
        }
      }
      const store = useCompletedDetails();

      store.prenatal_targeted_genome = [
        { key: "BILLING TO", value: this.billTo },
        { key: "FAMILY TYPE", value: this.familyType },
        { key: "FAMILY HISTORY", value: this.familyHx },
        {
          key: "FAMILY MEMBER PREVIOUSLY TESTED",
          value: this.familyPrevTested,
        },
        { key: "VCGS SAMPLE ID", value: this.posCtrlSampleId },

        { key: "RELATION TO POSITIVE CONTROL", value: this.rhipToPosCtrl },
        { key: "NUMBER OF VARIANTS ", value: this.numVariants },
        { key: "CLINICAL NOTES", value: this.clinicalNotes },
        {
          key: "PRICE",
          value: this.isFamSingleton ? this.dollarPrice(this.price) : null,
        },
      ];
      store.prenatal_targeted_genome_family = familyDetails;

      let result = {
        testType: this.testId,
        testCode: this.testCode,
        urgentReason: this.urgentReason,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        medicare: this.medicare,
        billTo: this.billTo,
        numVariants: this.numVariants,
        rhipToPosCtrl: this.rhipToPosCtrl,
        posCtrlName: this.posCtrlName,
        posCtrlBirthDate: this.reverseFormatDate(this.familyMemberDOBDisplay),

        posCtrlSampleId: this.posCtrlSampleId,
        familyPrevTested: this.familyPrevTested,
      };

      // Consent data is present
      if (this.$refs.PrenatalConsentContainer) {
        const consentData = this.$refs.PrenatalConsentContainer.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
